import React, { useState, useEffect, Fragment } from "react";
import { connect } from "react-redux";
import { firestoreConnect } from "react-redux-firebase";
import { compose } from "redux";
import FadeIn from "react-fade-in";
import Tooltip from "@mui/material/Tooltip";

import { Menu, Transition } from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";

import { addNewRoom, deleteRoom } from "../../redux/actions/gmActions";
import NewRoomModal from "./modals/NewRoomModal";
import {
  classNames,
  getRoomLimit,
  isSubscribed,
  shouldBlockFreePlanAccess,
} from "../../utils/Utils";

function Dashboard2(props) {
  const { account, rooms } = props;

  const [permissions, setPermissions] = useState({
    isAdmin: false,
    role: null,
    canEditLiveWindow: false,
  });
  const [roomsList, setRooms] = useState(rooms);
  const [roomLimit, setRoomLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [charge_failed, setChargeFailed] = useState(false);
  const [subscription_status, setSubscriptionStatus] = useState(null);
  const [subscription_active, setSubscriptionActive] = useState(false);
  const [searchWords, setSearchWords] = useState("");

  useEffect(() => {
    if (account) document.title = "Dashboard - Escape Room Studio";
  }, []);

  useEffect(() => {
    if (rooms) {
      const sortedRooms = [...rooms];
      const sortOrder = localStorage.getItem("roomSortOrder") || "date";
      switch (sortOrder) {
        case "date":
          sortedRooms.sort((a, b) => b.created_at - a.created_at);
          break;
        case "name":
          sortedRooms.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case "escape_rate":
          sortedRooms.sort((a, b) => {
            const rateA = a.stats
              ? (a.stats.wins / (a.stats.wins + a.stats.losses)) * 100
              : 0;
            const rateB = b.stats
              ? (b.stats.wins / (b.stats.wins + b.stats.losses)) * 100
              : 0;
            return rateB - rateA;
          });
          break;
        case "wins":
          sortedRooms.sort((a, b) => {
            const winsA = a.stats ? a.stats.wins : 0;
            const winsB = b.stats ? b.stats.wins : 0;
            return winsB - winsA;
          });
          break;
        case "losses":
          sortedRooms.sort((a, b) => {
            const lossesA = a.stats ? a.stats.losses : 0;
            const lossesB = b.stats ? b.stats.losses : 0;
            return lossesB - lossesA;
          });
          break;
      }
      setRooms(sortedRooms);
    }
  }, [rooms]);

  useEffect(() => {
    if (account) {
      const perms = account && account.permissions ? account.permissions : null;

      function search(id, myArray) {
        for (var i = 0; i < myArray.length; i++) {
          if (myArray[i].user_id === id) {
            return myArray[i];
          }
        }
      }

      if (perms) {
        const gm_permissions = search(props.uid, perms);
        if (gm_permissions) {
          setPermissions(gm_permissions);
          setLoading(false);
        }
      } else {
        setPermissions({ isAdmin: true });
        setLoading(false);
      }
    }

    if (account) {
      const plan_type = account ? account.subscription.product_id : null;
      const subscription_status_type =
        account && account.subscription_status
          ? account.subscription_status.type
          : null;

      // Plan Types
      setRoomLimit(getRoomLimit(account));

      // Set Charge Failed
      if (subscription_status_type === "charge.failed") setChargeFailed(true);

      // Set Subscription Status
      if (subscription_status_type)
        setSubscriptionStatus(subscription_status_type);

      // Set Subscription Active
      if (isSubscribed(account) || plan_type) setSubscriptionActive(true);
    }
  }, [account]);

  if (loading) {
    return (
      <div className="content ht-100v pd-0">
        <div
          className="text-center"
          style={{
            left: "50%",
            marginLeft: "-2em",
            position: "absolute",
            top: "40%",
          }}
        >
          <div
            className="spinner-border"
            style={{ width: "5rem", height: "5rem" }}
          ></div>
        </div>
      </div>
    );
  }

  const search = (search) => {
    console.log(search);
    setSearchWords(search);
  };

  const freePlanBlocked = shouldBlockFreePlanAccess(account);
  const roomLocked = (index) =>
    freePlanBlocked || (!subscription_active && index > 0);

  return (
    <div className="min-h-screen bg-[#f6f6f7] text-slate-900 antialiased">
      {charge_failed && (
        <div style={{ marginTop: "20px" }}>
          <div
            className="alert alert-danger d-flex align-items-center"
            role="alert"
            style={{ justifyContent: "space-between" }}
          >
            <div>
              <svg
                width="24px"
                fill="currentColor"
                style={{ marginRight: "10px" }}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 576 512"
              >
                <path d="M96 360C96 346.7 106.7 336 120 336H168C181.3 336 192 346.7 192 360C192 373.3 181.3 384 168 384H120C106.7 384 96 373.3 96 360zM224 360C224 346.7 234.7 336 248 336H360C373.3 336 384 346.7 384 360C384 373.3 373.3 384 360 384H248C234.7 384 224 373.3 224 360zM96 280C96 266.7 106.7 256 120 256H456C469.3 256 480 266.7 480 280C480 293.3 469.3 304 456 304H120C106.7 304 96 293.3 96 280zM456 128C469.3 128 480 138.7 480 152V200C480 213.3 469.3 224 456 224H376C362.7 224 352 213.3 352 200V152C352 138.7 362.7 128 376 128H456zM0 96C0 60.65 28.65 32 64 32H512C547.3 32 576 60.65 576 96V416C576 451.3 547.3 480 512 480H64C28.65 480 0 451.3 0 416V96zM48 96V416C48 424.8 55.16 432 64 432H512C520.8 432 528 424.8 528 416V96C528 87.16 520.8 80 512 80H64C55.16 80 48 87.16 48 96z" />
              </svg>{" "}
              <b>Payment Failed</b>
              <span style={{ marginLeft: "5px" }}>
                {" "}
                Please update your payment method.
              </span>
            </div>
            <a
              className="btn btn-xs btn-primary"
              style={{ marginLeft: "5px" }}
              href="/account/billing"
            >
              {" "}
              Go to billing
              <i
                className="fa-solid fa-arrow-right"
                style={{ marginLeft: "5px" }}
              ></i>
            </a>
          </div>
        </div>
      )}

      <div className="py-8 px-4 sm:px-0 max-w-[1300px] " style={{ margin: "0 auto" }}>
        <div className="flex items-center justify-between">
          <div className="mb-0">
            <p className="text-2xl font-semibold">Dashboard</p>
          </div>

          {/* View Format */}
          <div className="flex justify-between items-center mb-4 space-x-2">
            <div
              style={
                permissions.isAdmin || permissions.role === "Admin"
                  ? null
                  : { display: "none" }
              }
            >
              {subscription_active && (
                <a
                  href="#NewRoomModal"
                  data-toggle="modal"
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-500"
                >
                  <i className="fa-light fa-plus mr-2"></i>
                  Create Room
                </a>
              )}

              {rooms && rooms.length === 0 && (
                <a
                  href="#NewRoomModal"
                  data-toggle="modal"
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-500"
                >
                  <i className="fa-light fa-plus mr-2"></i>
                  Create Room
                </a>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  const currentView =
                    localStorage.getItem("roomViewFormat") || "grid";
                  const newView = currentView === "grid" ? "table" : "grid";
                  localStorage.setItem("roomViewFormat", newView);
                  // Force re-render
                  setRooms([...roomsList]);
                }}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
              >
                <i
                  className={`fa-light fa-${
                    localStorage.getItem("roomViewFormat") === "table"
                      ? "grid"
                      : "table"
                  } mr-2`}
                ></i>
                Switch to{" "}
                {localStorage.getItem("roomViewFormat") === "table"
                  ? "Grid"
                  : "Table"}{" "}
                View
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-4 mt-4">
          {/* Search */}
          {/* <div className="pt-6 pb-3 flex justify-end">
            <input
              className="bg-gray-100 py-2.5 px-4 text-black"
              placeholder="Search"
              onChange={(e) => search(e.target.value)}
            ></input>
          </div> */}

          {/* Sorting */}
          <div className="flex items-center space-x-4 mb-4">
            <span className="text-sm text-gray-600">Sort by:</span>
            <select
              className="bg-white border border-gray-300 text-gray-700 py-1 px-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
              value={localStorage.getItem("roomSortOrder") || "date"}
              onChange={(e) => {
                const value = e.target.value;
                localStorage.setItem("roomSortOrder", value);

                const sortedRooms = [...rooms];

                switch (value) {
                  case "date":
                    sortedRooms.sort((a, b) => b.created_at - a.created_at);
                    break;
                  case "name":
                    sortedRooms.sort((a, b) => a.name.localeCompare(b.name));
                    break;
                  case "escape_rate":
                    sortedRooms.sort((a, b) => {
                      const rateA = a.stats
                        ? (a.stats.wins / (a.stats.wins + a.stats.losses)) * 100
                        : 0;
                      const rateB = b.stats
                        ? (b.stats.wins / (b.stats.wins + b.stats.losses)) * 100
                        : 0;
                      return rateB - rateA;
                    });
                    break;
                  case "wins":
                    sortedRooms.sort((a, b) => {
                      const winsA = a.stats ? a.stats.wins : 0;
                      const winsB = b.stats ? b.stats.wins : 0;
                      return winsB - winsA;
                    });
                    break;
                  case "losses":
                    sortedRooms.sort((a, b) => {
                      const lossesA = a.stats ? a.stats.losses : 0;
                      const lossesB = b.stats ? b.stats.losses : 0;
                      return lossesB - lossesA;
                    });
                    break;
                  default:
                    break;
                }
                setRooms(sortedRooms);
              }}
            >
              <option value="date">Date Created</option>
              <option value="name">Name</option>
              <option value="escape_rate">Escape Rate</option>
              <option value="wins">Wins</option>
              <option value="losses">Losses</option>
            </select>
          </div>
        </div>

        <div>





          {/* Rooms */}
          <div>
            {roomsList && roomsList.length === 0 ? (
              <div className="py-12">
                <a
                  href="#NewRoomModal"
                  data-toggle="modal"
                  className="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-0 focus:ring-offset-2"
                >
                  <div className="flex items-center justify-center space-x-1 mb-4">
                    <i class="fa-kit fa-solid-door-open-circle-plus text-3xl"></i>
                  </div>
                  <span className="button-black-light">Create a new room</span>
                </a>
              </div>
            ) : localStorage.getItem("roomViewFormat") === "table" ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Escape Rate
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Wins
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Losses
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {roomsList
                      ? roomsList.map((item, index) => {
                          if (
                            !item.name
                              .toLowerCase()
                              .includes(searchWords.toLowerCase())
                          ) {
                            return null;
                          }
                          return (
                            <tr
                              key={index}
                              className={
                                roomLocked(index)
                                  ? "opacity-50"
                                  : ""
                              }
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <a
                                  href={
                                    roomLocked(index)
                                      ? null
                                      : "/r/" + item.id + "?view=puzzles"
                                  }
                                  className="text-sm font-medium text-gray-900 hover:text-black"
                                >
                                  {item.name}
                                </a>
                                {roomLocked(index) && (
                                  <p className="text-sm text-red-600">
                                    {freePlanBlocked
                                      ? "The free plan has ended. Subscribe to keep using this room."
                                      : "Please subscribe to reactivate."}
                                  </p>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.stats.escape_rate.toFixed(2)}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.stats.wins}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.stats.losses}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <div className="flex space-x-2">
                                  <a
                                    href={
                                      roomLocked(index)
                                        ? null
                                        : "/r/" + item.id + "?view=puzzles"
                                    }
                                    className={classNames(
                                      "inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium",
                                      roomLocked(index)
                                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                                        : "bg-blue-600 text-white hover:bg-blue-500"
                                    )}
                                  >
                                    Control Panel
                                  </a>
                                  <a
                                    href={
                                      roomLocked(index)
                                        ? null
                                        : "/settings/game/" + item.id
                                    }
                                    className={classNames(
                                      "inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium",
                                      roomLocked(index)
                                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                                        : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                                    )}
                                  >
                                    <i className="fa-light fa-palette mr-2" />
                                    Customize
                                  </a>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      : null}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="pb-12 grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
                {roomsList
                  ? roomsList.map((item, index) => {
                      const stats = item.stats ? item.stats : null;

                      if (
                        !item.name
                          .toLowerCase()
                          .includes(searchWords.toLowerCase())
                      ) {
                        console.log("NO", item.name);
                        return null;
                      } else {
                        return (
                          <div
                            key={index}
                            className={classNames(
                              "w-full h-full bg-white rounded-md border border-gray-200 hover:border-gray-300 hover:shadow transition-all duration-200",
                              roomLocked(index)
                                ? "opacity-50"
                                : "opacity-100"
                            )}
                          >
                            <div className="p-6">
                              <div className="flex items-start justify-between mb-6">
                                <div>
                                  <a
                                    href={
                                      roomLocked(index)
                                        ? null
                                        : "/r/" + item.id + "?view=puzzles"
                                    }
                                    target="_blank"
                                    className="text-xl font-semibold text-gray-900 hover:text-black transition-colors"
                                  >
                                    {item.name}
                                  </a>

                                  <div className="flex items-center space-x-6 mt-3">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-medium text-gray-600">
                                        Win Rate
                                      </span>
                                      <span className="text-sm font-semibold text-gray-900">
                                        {item.stats.escape_rate.toFixed(2)}%
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-medium text-gray-600">
                                        Wins
                                      </span>
                                      <span className="text-sm font-semibold text-gray-900">
                                        {item.stats.wins}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-medium text-gray-600">
                                        Losses
                                      </span>
                                      <span className="text-sm font-semibold text-gray-900">
                                        {item.stats.losses}
                                      </span>
                                    </div>
                                  </div>

                                  {roomLocked(index) && (
                                    <p className="mt-2 text-sm text-red-600">
                                      {freePlanBlocked
                                        ? "The free plan has ended. Subscribe to keep using this room."
                                        : "Please subscribe to reactivate."}
                                    </p>
                                  )}
                                </div>

                                <Menu as="div" className="relative">
                                  <Menu.Button className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors">
                                    <span className="sr-only">
                                      Open options
                                    </span>
                                    <EllipsisHorizontalIcon
                                      className="w-5 h-5 text-gray-500"
                                      aria-hidden="true"
                                    />
                                  </Menu.Button>

                                  <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                  >
                                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                      <div className="py-1">
                                        <Menu.Item>
                                          {({ active }) => (
                                            <a
                                              href={"/settings/game/" + item.id}
                                              className={classNames(
                                                active ? "bg-gray-50" : "",
                                                "block px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
                                              )}
                                            >
                                              Settings
                                            </a>
                                          )}
                                        </Menu.Item>
                                      </div>
                                    </Menu.Items>
                                  </Transition>
                                </Menu>
                              </div>

                              <div className="flex space-x-3">
                                <a
                                  href={
                                    roomLocked(index)
                                      ? null
                                      : "/r/" + item.id + "?view=puzzles"
                                  }
                                  className={classNames(
                                    "flex items-center px-4 py-2 rounded-md font-medium text-sm transition-colors",
                                    roomLocked(index)
                                      ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                                      : "bg-blue-600 text-white hover:bg-blue-500"
                                  )}
                                >
                                  Control Panel
                                  {roomLocked(index) && (
                                    <span className="w-2 h-2 ml-2 rounded-full bg-red-500" />
                                  )}
                                </a>

                                <a
                                  href={
                                    roomLocked(index)
                                      ? null
                                      : "/settings/game/" + item.id
                                  }
                                  className={classNames(
                                    "flex items-center px-4 py-2 rounded-md font-medium text-sm transition-colors",
                                    roomLocked(index)
                                      ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                                      : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                                  )}
                                >
                                  <i className="fa-light fa-palette mr-2" />
                                  Customize
                                  {roomLocked(index) && (
                                    <span className="w-2 h-2 ml-2 rounded-full bg-red-500" />
                                  )}
                                </a>
                              </div>
                            </div>
                          </div>
                        );
                      }
                    })
                  : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <NewRoomModal />
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  console.log("state2", state);

  const account_id = state.firebase.profile.account_id
    ? state.firebase.profile.account_id
    : null;

  return {
    rooms: state.firestore.ordered.gamemaster,
    auth: state.firebase.auth,
    account_id: account_id,
    uid: state.firebase.auth.uid ? state.firebase.auth.uid : "123",
    profile: state.firebase.profile,
    app: state.app,
    account: state.firestore.ordered.user_accounts[0],
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    addNewRoom: (name) => dispatch(addNewRoom(name)),
    deleteRoom: (room_id) => dispatch(deleteRoom(room_id)),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  firestoreConnect((props) => [
    {
      collection: "gamemaster",
      where: [["account_id", "==", props.account_id]],
      orderBy: ["created_at", "desc"],
    }
  ])
)(Dashboard2);
